import { Box, Button, Card, CardContent, Divider, Grid, Typography } from '@mui/material';
import { PaymentElement } from '@stripe/react-stripe-js';
import { useEffect, useState } from 'react';
import { Stripe, StripeElements } from '@stripe/stripe-js';
import { useAppSelector } from '../../redux/hooks';
import { RootState } from '../../redux/store';
import { PlanType } from '../../utils/enums/plan-type.enum';
import LogoLink from '../LogoLink/LogoLink';

interface UpdatePaymentProps {
  stripe: Stripe | null;
  elements: StripeElements | null;
}

const UpdatePayment = ({ stripe, elements }: UpdatePaymentProps) => {
  const plan = useAppSelector((state: RootState) => state.subscription.productDetails);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const handleSubmit = async (event?: React.FormEvent<HTMLFormElement>) => {
    event?.preventDefault();

    if (!stripe || !elements) {
      return;
    }
    let result;
    if (plan?.name === PlanType.PREMIUM) {
      result = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${process.env.REACT_APP_BASE_URL}/activation-success`,
        },
      });
    } else {
      result = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: `${process.env.REACT_APP_BASE_URL}/activation-success`,
        },
      });
    }
    if (result.error) {
      setErrorMessage(result.error.message || 'An unknown error occurred');
    }
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSubmit();
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <Grid
      display="flex"
      flexDirection="column"
      alignItems="center"
      gap={3}
      sx={{
        maxWidth: '355px',
        backgroundColor: '#FFFFFF',
        padding: '24px',
        borderRadius: '8px',
        border: '1px solid #DCE0E5',
        boxShadow: '0px 1px 2px rgba(128, 128, 128, 0.12)',
      }}
    >
      <Box display="flex" flexDirection="column" alignItems="center" gap="10px" padding="3%">
        <LogoLink padding="56px" width="173.343px" height="32px" />
      </Box>
      <Typography variant="title5Bold" textAlign="center">
        Please enter your Payment Details
      </Typography>
      <Typography
        sx={{ fontSize: '16px', fontFamily: 'Inter', fontWeight: 400, lineHeight: 'normal' }}
        textAlign="center"
        color="black.contrastText"
        marginTop={2}
      >
        As part of our streamlined payment process, we will securely store your billing details
      </Typography>
      <Box display="flex" flexDirection="column" alignItems="center" gap={2} width="100%" padding="10px 20px 20px">
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <input type="text" hidden />
          <Card
            variant="outlined"
            style={{
              width: '100%',
              borderRadius: '4px',
              boxShadow: 'none',
              borderColor: '#EAEAEA',
              marginBottom: '16px',
            }}
          >
            <CardContent>
              <Typography variant="button" component="div" sx={{ fontWeight: 'bold', marginBottom: 1 }}>
                Your subscription
              </Typography>

              <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ marginBottom: 1 }}>
                <Typography sx={{ color: 'text.secondary' }} variant="subheadRegular">
                  {plan?.name}
                </Typography>
                {plan?.name !== PlanType.CORPORATE && plan?.name !== PlanType.BASIC && (
                  <Typography sx={{ color: 'text.secondary' }} variant="subheadRegular">
                    ${plan?.baseValue} billed monthly
                  </Typography>
                )}
              </Box>
              {plan?.name !== PlanType.CORPORATE && plan?.name !== PlanType.BASIC && (
                <>
                  <Divider light />
                  <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ marginTop: 1 }}>
                    <Typography style={{ fontWeight: 'bold' }} variant="button">
                      Total
                    </Typography>
                    <Typography sx={{ fontWeight: 'bold' }} variant="button">
                      ${plan?.baseValue}
                    </Typography>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
          <PaymentElement />
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={!stripe}
            style={{ marginTop: '24px', width: '100%', padding: '10px' }}
          >
            Subscribe
          </Button>
          {errorMessage && (
            <Typography style={{ fontWeight: 'bold', color: '#B22A2A', marginTop: '5px' }} variant="button">
              {errorMessage}
            </Typography>
          )}
        </form>
      </Box>
    </Grid>
  );
};
export default UpdatePayment;
